import React from "react";
import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import ImageSlider from "./ImageSlider";

function PageHeader({ title, subtitle }) {
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.16), rgba(220,36,44,0.14))"
  );
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  return (
    <Box
      pt={{ base: "140px", md: "140px" }}
      pb={{ base: 10, md: 12 }}
      bg={heroBg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Container maxW="1200px">
        <Stack spacing={3}>
          <HStack spacing={2} flexWrap="wrap">
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
              border="1px solid"
              borderColor={border}
              fontWeight="800"
            >
              University of Ottawa
            </Tag>
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
              color={accent}
              fontWeight="900"
            >
              SITE-CAV
            </Tag>
          </HStack>

          <Heading color={titleColor} fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="tight">
            {title}
          </Heading>

          {subtitle ? (
            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");

  return (
    <Box py={{ base: 10, md: 12 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 5, md: 7 }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        <Divider borderColor={border} mb={{ base: 6, md: 8 }} />
        {children}
      </Container>
    </Box>
  );
}

function PillarCard({ k, v }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const bg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box
      border="1px solid"
      borderColor={border}
      bg={bg}
      borderRadius="22px"
      p={{ base: 5, md: 6 }}
      boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
    >
      <Text fontSize="xs" letterSpacing="0.14em" fontWeight="900" color={subColor}>
        {k.toUpperCase()}
      </Text>
      <Text mt={2} fontWeight="900" color={titleColor} fontSize={{ base: "md", md: "lg" }}>
        {v}
      </Text>
    </Box>
  );
}

function SliderFrame({ children }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const bg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  return (
    <Box
      mt={5}
      border="1px solid"
      borderColor={border}
      borderRadius="24px"
      overflow="hidden"
      bg={bg}
      maxW={{ base: "100%", md: "900px" }}
      mx="auto"
      boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
    >
      {children}
    </Box>
  );
}

function StyledAccordion({ isMobile }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#315369", "white");
  const bodyColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  const Item = ({ title, children }) => (
    <AccordionItem border="0">
      {({ isExpanded }) => (
        <Box
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          overflow="hidden"
          bg={panelBg}
        >
          <AccordionButton
            px={{ base: 5, md: 6 }}
            py={{ base: 4, md: 5 }}
            _hover={{ bg: useColorModeValue("rgba(49,83,105,0.06)", "rgba(255,255,255,0.05)") }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="900"
              color={titleColor}
            >
              <Box
                as="span"
                display="inline-block"
                w="10px"
                h="10px"
                borderRadius="999px"
                bg={accent}
                mr={3}
                transform={isExpanded ? "scale(1)" : "scale(0.7)"}
                opacity={isExpanded ? 1 : 0.6}
                transition="all 160ms ease"
              />
              {title}
            </Box>
            <AccordionIcon />
          </AccordionButton>

          <AccordionPanel px={{ base: 5, md: 6 }} pb={{ base: 5, md: 6 }}>
            <Text
              color={bodyColor}
              fontSize={{ base: "md", md: "lg" }}
              textAlign={isMobile ? "left" : "left"}
            >
              {children}
            </Text>
          </AccordionPanel>
        </Box>
      )}
    </AccordionItem>
  );

  return (
    <Accordion allowMultiple defaultIndex={[0]} display="grid" gap={5}>
      <Item title="Workshop">
        <b>The Annual SITE-CAV Workshop</b> is a key event where trainees engage with industry and academic experts through talks, panels, and 
        research showcases. This gathering fosters networking opportunities and explores themes aligned with the program's scope
        <SliderFrame>
          <ImageSlider images={["N-hackathon 2025.jpg"]} />
        </SliderFrame>
      </Item>

      <Item title="Hackathon">
        <b>The SITE-CAV Hackathon</b> is an annual event that brings together second-year graduate students from the projects. Co-chaired by two 
        co-applicants, this intensive 48-hour event challenges students to collaborate in diverse teams and tackle research problems related to 
        autonomous decision-making for AV.
        <SliderFrame>
          <ImageSlider images={["N-hackathon 2025.jpg"]} />
        </SliderFrame>
      </Item>

      <Item title="Seminars">
        <b>The SITE-CAV seminars</b> demonstrate a broad spectrum of topics, including breakthrough research, technology commercialization, 
        and entrepreneurship in Connected and Autonomous Vehicles. Bringing together expert speakers from around the world, these sessions 
        provide a valuable forum for knowledge exchange, collaboration, and professional networking.
      </Item>
    </Accordion>
  );
}

export default function Training() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const bodyColor = useColorModeValue("gray.700", "gray.300");
  const subtleBg = useColorModeValue("rgba(49,83,105,0.04)", "rgba(255,255,255,0.03)");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");

  return (
    <Box>
      <PageHeader
        title="Program"
        subtitle="Research, training, commercialization, internships, and outreach activities within SITE-CAV."
      />

      <Section
        title="Program Summary"
        subtitle="SITE-CAV (Secure, Intelligent and Trustworthy Ecosystem of Connected and Autonomous Vehicles) is a collaborative and interdisciplinary project to enable smooth integration of connected and autonomous vehicles (CAVs) into the evolving transportation ecosystem in Ontario."
      >
        <Stack spacing={6}>
          <Box
            border="1px solid"
            borderColor={border}
            bg={subtleBg}
            borderRadius="24px"
            p={{ base: 6, md: 8 }}
          >
            <Text color={bodyColor} fontSize={{ base: "md", md: "lg" }} textAlign="left">
              Research questions that will be addressed by the project team involve the development of efficient and reliable communication protocols 
              integrated with security-by-design solutions along with intelligent, user friendly and legal-aware decision support systems. 
              Four key pillars of the research project.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <PillarCard
              k="Pillar 1"
              v="Complementary expertise across 5G/6G, vehicular networking, security, human factors, and legal implications."
            />
            <PillarCard
              k="Pillar 2"
              v="Two experimental testbeds: uOttawa AV networking testbed and UofT driving simulation for human factors."
            />
            <PillarCard
              k="Pillar 3"
              v="Strong commercialization plan with industry engagement for security, decision support, and ultra-fast connectivity."
            />
            <PillarCard
              k="Pillar 4"
              v="Experiential learning via uOttawa Cyber Range for cyber-attack management and organizational response in CAV settings."
            />
          </SimpleGrid>

          <Text color={bodyColor} fontSize={{ base: "md", md: "lg" }} textAlign="left">
            These experimental capabilities are designed to be bridged with Western University’s optimized computing and
            communications lab focusing on network virtualization, resiliency, and big data challenges related to 5G/6G
            networks.
          </Text>
        </Stack>
      </Section>

      <Section
        title="Research & Training"
        subtitle="Workshops, hackathons, and seminars that support trainee development and industry engagement."
      >
        <StyledAccordion isMobile={isMobile} />
      </Section>

      <Section title="Commercialization & Intellectual Property">
        <Text color={bodyColor} fontSize={{ base: "md", md: "lg" }} textAlign="left">
          SITE-CAV is developing efficient and reliable communication protocols integrated with security-by-design frameworks; 
          along with smart, user friendly and legal-aware decision support. This will help ensure Ontario develops connected and 
          autonomous transportation systems to handle increased traffic, while reducing emissions.
        </Text>
      </Section>

      <Section title="Internships">
        <Text color={bodyColor} fontSize={{ base: "md", md: "lg" }} textAlign="left">
          The trainees of the SITE-CAV project connect with a large number of industry partners, and work directly with them on 
          deployment and integration challenges of the secure, trustworthy and intelligent CAV ecosystem through various means of 
          collaborations including externally funded research projects and internship. In many cases this is expected to lead to job 
          opportunities with these graduates and postdocs.
        </Text>
      </Section>

      <Section title="Youth Outreach">
        <Stack spacing={6}>
          <Text color={bodyColor} fontSize={{ base: "md", md: "lg" }} textAlign="left">
            The Youth Outreach Plan is divided into two categories, namely those events we stage and those that are ad hoc. In respect to 
            events we stage, primarily at uOttawa, there are Annual Workshops comprised of seminars and poster sessions to which local 
            youth will be invited through means discussed below. In addition, there are opportunities around the workshops to have tours 
            of the Smart Connected Vehicles Innovation Centre, which includes an indoor AV test track. Graduate students will provide 
            discussion and demonstrations at the Centre.
            <br />
            <br />
            The Faculty of Engineering at uOttawa frequently hosts public events for pre-university youth such as maker space projects, 
            competitions, lab tours and lectures. The project team EM will liaise with Faculty organizers of these events to offer participation 
            by members of the SITE-CAV project team over the course of the project.
          </Text>

          <SliderFrame>
            <ImageSlider
              images={[
                "ACM-Women Student Chapter 1.jpg",
                "ACM-Women Student Chapter 2.jpg",
                "ACM-Women Student Chapter 3.jpg",
                "ACM-Women Student Chapter 4.jpg",
                "ACM-Women Student Chapter 5.jpg",
                "ACM-Women Student Chapter 6.jpg",
              ]}
            />
          </SliderFrame>

          <Box maxW="900px" mx="auto">
            <Text color={useColorModeValue("gray.600", "gray.300")} fontSize={{ base: "sm", md: "md" }} textAlign="left">
              The University of Ottawa ACM-Women Student Chapter participated in The Hour of AI initiative as part of the Computer Science 
              Education Week (Dec 8-12). Our passionate volunteers spent half a day at Elgin Street Public School, working hands-on with Grade 
              3–6 students to introduce core concepts of Artificial Intelligence and Machine Learning. We were genuinely impressed by the 
              students’ level of understanding. What stood out even more, however, was that their questions and concerns about AI outweighed 
              their excitement. This opened the door to meaningful discussions around, the human role in shaping AI, the importance of unbiased 
              and responsible data, how AI decisions can impact society and communities. Together, we trained a simple machine-learning model to 
              identify objects that do not belong in water, turning abstract concepts into tangible learning moments. This experience was mutually 
              rewarding: inspiring curiosity and critical thinking among young learners, while reminding us how essential early, responsible AI 
              education is for the future we are collectively building.
            </Text>
          </Box>
              
          <Divider maxW="900px" mx="auto" borderColor={useColorModeValue("gray.300", "gray.600")} />
          
          <SliderFrame>
            <ImageSlider
              images={[
                "event_2_1.jpg",
                "event_2_2.jpg",
                "event_2_3.jpg",
                "event_2_4.jpg",
              ]}
            />
          </SliderFrame>

          <Box maxW="900px" mx="auto">
            <Text color={useColorModeValue("gray.600", "gray.300")} fontSize={{ base: "sm", md: "md" }} textAlign="left">
              Students from Command Add Girls visiting the uOttawa Kanata North campus, exploring the Smart Connected Vehicles 
              Innovation Centre (SCVIC) during a guided tour of the labs and research spaces. The visit offered a close look at 
              real-world technology projects and the pathways into careers in tech.
            </Text>
          </Box>



        </Stack>
      </Section>

      {/* bottom spacing */}
      <Box pb={{ base: 10, md: 14 }} />
    </Box>
  );
}
